import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ApiService from "../../services/api-service";
import { Images } from "../../assets/common";
import notifier from "../Notifier";
import { asList } from "../../utils/gamification/insights";
import { formatCount } from "../../utils/gamification/format";
import { INK, Muted, Row, SectionLabel } from "../Ink";
import RewardDialog, { REWARD_KINDS } from "./RewardDialog";
import { fanFacts, fanId, fanImage, fanName } from "./fan";

// Five rows show; the rest scroll inside the panel so it never stretches the page.
export const ROW_HEIGHT = 52;
export const VISIBLE_ROWS = 5;

const listSx = {
  maxHeight: ROW_HEIGHT * VISIBLE_ROWS,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,255,255,0.18) transparent",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.18)", borderRadius: 3 },
  "& > :last-child": { borderBottom: 0 },
};

const rowSx = {
  height: ROW_HEIGHT,
  py: 0,
  boxSizing: "border-box",
  "&:hover .ink-reward": { color: INK.accent },
  "&:focus-visible": { outline: `1px solid ${INK.accent}`, outlineOffset: -1 },
};

function Avatar({ src }) {
  const fallback = (e) => {
    if (e.currentTarget.dataset.fallback) return;
    e.currentTarget.dataset.fallback = "1";
    e.currentTarget.src = Images.dummy;
  };
  return (
    <Box
      component="img" src={src || Images.dummy} alt="" onError={fallback}
      sx={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", display: "block", bgcolor: "rgba(255,255,255,0.06)" }}
    />
  );
}

// Owner-only panel on an artist profile: the top fans of the last 30 days.
// Clicking a fan opens the reward dialog (`POST /engagement/artist/reward`).
export default function TopFansPanel({ artistId, sx }) {
  const [fans, setFans] = useState(null);
  const [pending, setPending] = useState(null); // the fan being rewarded
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!artistId) return undefined;
    let alive = true;
    ApiService.ENGAGEMENT_ARTIST_TOP_FANS(artistId)
      .then((res) => alive && setFans(asList(res?.result ?? res, "fans", "topFans")))
      .catch((error) => {
        console.error("[TopFansPanel] top-fans failed", error?.message);
        if (alive) setFans([]);
      });
    return () => {
      alive = false;
    };
  }, [artistId]);

  const send = async ({ kind, ...extra }) => {
    if (!pending) return;
    setBusy(true);
    try {
      await ApiService.ENGAGEMENT_ARTIST_REWARD({ fanId: fanId(pending), kind, ...extra });
      notifier.success(`${REWARD_KINDS[kind]?.label || "Reward"} sent to ${fanName(pending)}`);
      setPending(null);
    } catch (error) {
      console.error("[TopFansPanel] reward failed", error?.message);
      notifier.error(error?.response?.data?.message || "Could not send the reward");
    } finally {
      setBusy(false);
    }
  };

  const openFor = (fan) => (e) => {
    if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    setPending(fan);
  };

  if (fans === null) return null;

  return (
    <Box sx={sx}>
      <SectionLabel sx={{ mt: 0 }} right={fans.length > VISIBLE_ROWS ? `${fans.length} fans · 30 days` : "30 days"}>Top fans</SectionLabel>
      {fans.length === 0 ? <Muted>No fan activity yet. Points appear once people listen, watch and share.</Muted> : null}
      {fans.length > 0 ? (
        <Box aria-label="Top fans" sx={listSx}>
          {fans.map((fan, i) => {
            const name = fanName(fan);
            return (
              <Row
                key={fanId(fan) || i}
                role="button"
                tabIndex={0}
                aria-label={`Reward ${name}`}
                onClick={openFor(fan)}
                onKeyDown={openFor(fan)}
                sx={rowSx}
                leading={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Typography sx={{ width: 18, fontSize: 12, color: INK.dim, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fan.rank || i + 1}</Typography>
                    <Avatar src={fanImage(fan)} />
                  </Box>
                }
                primary={name}
                secondary={fanFacts(fan) || null}
                right={
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK.text, lineHeight: 1.2 }}>
                      {formatCount(fan.points)}
                      <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 400, color: INK.muted }}>pts</Box>
                    </Typography>
                    <Typography className="ink-reward" sx={{ fontSize: 11, color: INK.muted, lineHeight: 1.2, mt: 0.25 }}>Reward</Typography>
                  </Box>
                }
              />
            );
          })}
        </Box>
      ) : null}
      {pending ? (
        <RewardDialog key={fanId(pending)} open fanName={fanName(pending)} busy={busy} onClose={() => setPending(null)} onSubmit={send} />
      ) : null}
    </Box>
  );
}
